import clsx from "clsx";
import React, { useState } from "react";
import { ITemplate } from "../utils/Types";
import { NavLink, useParams } from "react-router-dom";
import { CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import { truncate } from "lodash";
import {
  FileText,
  Gear,
  Sparkle,
  NotePencil,
  Check,
} from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VersionBadge } from "./version-badge";
import { useAppSelector } from "@/hooks";
import { USER_ROLES } from "@/utils/constants";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";
import { useNavigate } from "react-router-dom";

interface TemplateProps {
  template: ITemplate;
  icon: React.ReactNode;
  category?: string;
}

const Template: React.FC<TemplateProps> = ({ template, icon, category }) => {
  const { templateId = "" } = useParams<{
    templateId: string;
  }>();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user.user);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const { setPendingNavigation } = useUnsavedChangesStore();

  const itemClass = clsx(
    // "bg-gray-25/10 dark:bg-gray-900 shadow-md rounded-lg border-[0.5px] border-gray-25 dark:border-gray-500 hover:bg-gray-25/50 dark:hover:bg-gray-900/50 ",
    "dark:bg-gray-900 shadow-md rounded-lg border-[0.5px] border-gray-25 dark:border-gray-500 hover:bg-gray-25/50 dark:hover:bg-gray-900/50 ",
    templateId ? "bg-gray-25/10  " : "bg-white ",
    {
      "bg-sky-25/50 hover:border-sky-100 dark:bg-sky-600/50 border-sky-200 dark:border-sky-200 hover:bg-sky-25 dark:hover:bg-sky-600/80":
        window.location.pathname.startsWith("/dashboard/shared-templates")
          ? template?.public_access_key == templateId
          : template?.id == templateId,
    }
  );

  const content = (
    <div className={clsx("flex flex-col p-3 max-h-[112px]", {})}>
      <div className="flex items-center justify-between">
        <div
          className={
            "flex items-center text-sm font-medium mr-2 truncate text-sky-200 dark:text-sky-50 " +
            (template?.id == templateId ? "text-sky-200 dark:text-white" : "")
          }
        >
          <Tooltip>
            <TooltipContent side="top" className="bg-gray-600 text-white">
              <p>{category}</p>
            </TooltipContent>
            <TooltipTrigger asChild>
              <div className="w-fit mr-2">{icon || <FileText size={20} />}</div>
            </TooltipTrigger>
          </Tooltip>
          {template?.title
            ? truncate(template?.title, { length: 30 })
            : "New Template"}
        </div>

        <div className="flex items-center">
          {template?.is_active && (
            <div className="ml-auto mt-auto">
              <VersionBadge
                icon={<Check size={15} />}
                className={
                  "flex items-center justify-center overflow-hidden transition-all duration-300 ease-in-out border-[1px] " +
                  "border-green-300 dark:border-green-500 rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 hover:bg-green-50 hover:dark:bg-green-950 hover:border-green-300 hover:dark:border-green-500 hover:text-green-700 hover:dark:text-green-400"
                }
                text={"Active"}
              />
            </div>
          )}
          {!isAdmin && (
            <div className="ml-2 mt-auto">
              <VersionBadge
                icon={
                  template?.default ? (
                    template?.updated ? (
                      <NotePencil size={15} />
                    ) : (
                      <Gear size={15} />
                    )
                  ) : (
                    <Sparkle size={15} />
                  )
                }
                className={
                  "flex items-center justify-center overflow-hidden transition-all duration-300 ease-in-out border-[1px] " +
                  (template?.default
                    ? template?.updated
                      ? "border-blue-300 dark:border-blue-25 rounded-full bg-blue-25 dark:bg-blue-950 text-blue-700 dark:text-blue-100 hover:bg-blue-25 hover:dark:bg-blue-950 hover:border-blue-300 hover:dark:border-blue-25 hover:text-blue-700 hover:dark:text-blue-50"
                      : "border-gray-300 dark:border-gray-50 rounded-full bg-gray-25 dark:bg-gray-950 text-gray-700 dark:text-gray-50 hover:bg-gray-25 hover:dark:bg-gray-950 hover:border-gray-300 hover:dark:border-gray-50 hover:text-gray-700 hover:dark:text-gray-50"
                    : "border-sky-300 dark:border-sky-50 rounded-full bg-sky-25 dark:bg-sky-950 text-sky-700 dark:text-sky-100 hover:bg-sky-25 hover:dark:bg-sky-950 hover:border-sky-300 hover:dark:border-sky-50 hover:text-sky-700 hover:dark:text-sky-50")
                }
                text={
                  template?.default
                    ? template?.updated
                      ? "Modified"
                      : "Default"
                    : "Custom"
                }
              />
            </div>
          )}
        </div>
      </div>
      {(template?.latest_version?.description || template?.description) && (
        <span className="text-sm font-medium mr-2 mt-1 truncate text-gray-500 dark:text-gray-100">
          {template?.latest_version?.draft
            ? template?.latest_version?.description
            : template?.description}
        </span>
      )}
      <div
        className={clsx("flex", {
          "mt-2": !template?.latest_version?.description,
        })}
      >
        {(user.role === USER_ROLES.ADMIN ||
          (template?.default && template?.updated) ||
          (!template?.default && template?.created_at)) &&
          !isBadgeHovered && (
            <div className="flex items-center">
              <div
                className={
                  "flex items-center text-gray-100 mt-2 text-xs " +
                  (template?.id == templateId
                    ? "text-gray-200 dark:text-white"
                    : "")
                }
              >
                <CalendarIcon
                  className={
                    "h-4 w-4 mr-1 text-gray-100 " +
                    (template?.id == templateId
                      ? "text-gray-200 dark:text-white"
                      : "")
                  }
                />
                {new Date(
                  template?.updated!
                    ? template?.updated_at!
                    : template?.created_at!
                ).toDateString()}
              </div>
              <div
                className={
                  "flex items-center text-gray-100 mt-2 ml-2 text-xs " +
                  (template?.id == templateId
                    ? "text-gray-200 dark:text-white"
                    : "")
                }
              >
                <ClockIcon
                  className={
                    "h-4 w-4 mr-1 text-gray-100 " +
                    (template?.id == templateId
                      ? "text-gray-200 dark:text-white"
                      : "")
                  }
                />
                {new Date(
                  template?.updated!
                    ? template?.updated_at!
                    : template?.created_at!
                ).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          )}
      </div>
    </div>
  );

  const handleNavigation = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    event.preventDefault();
    if (templateId) {
      setPendingNavigation(
        window.location.pathname.startsWith("/dashboard/shared-templates")
          ? `/dashboard/shared-templates/${template?.public_access_key}`
          : `${isAdmin ? "/admin" : ""}/dashboard/templates/${template?.id}`
      );
    } else {
      setPendingNavigation(null);
      navigate(
        window.location.pathname.startsWith("/dashboard/shared-templates")
          ? `/dashboard/shared-templates/${template?.public_access_key}`
          : `${isAdmin ? "/admin" : ""}/dashboard/templates/${template?.id}`
      );
    }
  };

  return (
    <li className={itemClass}>
      {
        <NavLink
          onClick={(event) => handleNavigation(event)}
          to={
            window.location.pathname.startsWith("/dashboard/shared-templates")
              ? `/dashboard/shared-templates/${template?.public_access_key}`
              : `${isAdmin ? "/admin" : ""}/dashboard/templates/${template?.id}`
          }
          className={({ isActive }) =>
            clsx("relative focus:outline-slate-500", {
              "text-slate-400": isActive,
              "block text-slate-800": !isActive,
            })
          }
        >
          {content}
        </NavLink>
      }
    </li>
  );
};

export default Template;
